import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { currentUser } from '../api/auth';
import AccessDenied from '../components/AccessDenied';
import Header from '../components/Header';
import { canUseReceiving } from '../lib/roles';
import { t } from '../lib/i18n';
import type { User } from '../api/types';

/**
 * Three-tab umbrella for the v2.24 Purchasing module. Renders Header + a
 * NavLink-based tab strip; each tab maps to a child route so the URL stays
 * shareable. Owner/manager only — accounting and staff hit AccessDenied to
 * match the existing /vendors role rules.
 */
export default function PurchasingLayout() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  const isAuthorized = user.role === 'owner' || user.role === 'manager';
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} /></main>
      </div>
    );
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    'inline-flex items-center px-3 py-2 text-sm border-b-2 -mb-px ' +
    (isActive
      ? 'border-tremor-brand text-tremor-content-strong font-semibold'
      : 'border-transparent text-tremor-content hover:text-tremor-content-strong');

  return (
    <div className="min-h-screen flex flex-col">
      {/* Shared store picker only on the tabs whose data honors it. */}
      <Header user={user} storeFilter={!pathname.startsWith('/purchasing/suppliers')} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="border-b border-tremor-border flex gap-1 overflow-x-auto">
          <NavLink to="/purchasing/suppliers"  className={tabClass}>{t.purchasing.tabs.suppliers}</NavLink>
          <NavLink to="/purchasing/purchases"  className={tabClass}>{t.purchasing.tabs.purchases}</NavLink>
          <NavLink to="/purchasing/receptions" className={tabClass}>{t.purchasing.tabs.receptions}</NavLink>
          {/* Order Reception ("Recepción") — the scanner-driven receiving
              workflow, distinct from the read-only "Recepciones" (plural)
              history tab above. Standalone route (/po/scan), not nested
              under this layout's <Outlet>, so it renders its own Header. */}
          {canUseReceiving(user) && (
            <NavLink to="/po/scan" className={tabClass}>{t.recepcion.navLabel}</NavLink>
          )}
          <NavLink to="/purchasing/returns"    className={tabClass}>{t.purchasing.tabs.returns}</NavLink>
        </div>
        <Outlet context={user} />
      </main>
    </div>
  );
}
